import { useEffect, useRef, useState } from "react";
import i18n from "@/utils/i18n";
import {
  decodeJwtPayload,
  extractJitsiUserFromToken,
  type JitsiUserInfo,
} from "@/lib/jitsi-jwt";

export { decodeJwtPayload, extractJitsiUserFromToken };

interface JitsiEmbedProps {
  /** Backend'den gelen tam Jitsi URL'i (örn. https://meet.jit.si/euas-xxx). */
  externalUrl: string;
  /** İsteğe bağlı JWT — sadece public domain'de değilse iletilir. */
  token?: string | null;
  /** Kullanıcı kimliğini Jitsi'ye geç (prejoin formu doldurulmasın diye). */
  user?: JitsiUserInfo;
  /** Kullanıcı odaya fiilen girdiğinde tetiklenir (giriş-çıkış kaydı için). */
  onJoin?: () => void;
  /** Kullanıcı toplantıdan ayrıldığında / kapattığında tetiklenir. */
  onLeave?: () => void;
  /**
   * Kullanıcı görüşmeyi bitirdi (kırmızı "ayrıl" butonu → Jitsi `readyToClose`).
   * Bundan sonra iframe Jitsi'nin kendi karşılama sayfasını ("En son
   * görüşülenler listeniz boş") gösterir; sayfa bu anda kendi ekranına dönmeli.
   */
  onClose?: () => void;
  /** IFrame API örneği oluşunca (komut göndermek için) ve kapanınca null ile çağrılır. */
  onApiReady?: (api: JitsiMeetExternalAPIInstance | null) => void;
  /** Odadaki başka bir katılımcının `sendEndpointTextMessage` ile gönderdiği metin. */
  onEndpointTextMessage?: (text: string, senderId: string | null) => void;
  className?: string;
}

// Window.JitsiMeetExternalAPI tek tanım: src/types/jitsi.d.ts
type JitsiApi = JitsiMeetExternalAPIInstance;

const SCRIPT_ID = "jitsi-external-api-script";
// Public meet.jit.si JWT validasyonu yapıyor; backend'in ürettiği token
// `iss` boş olduğu için reddediliyor. Bu domain'lerde JWT'yi atla.
const PUBLIC_JITSI_DOMAINS = new Set(["meet.jit.si", "8x8.vc"]);

// Jitsi external_api ve nginx HTTPS üzerinden serve edilir (lokal dev'de
// self-signed cert, prod'da Let's Encrypt). Tek protokol: HTTPS.

function loadJitsiScript(domain: string): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.JitsiMeetExternalAPI) {
    return Promise.resolve();
  }
  const existing = document.getElementById(
    SCRIPT_ID,
  ) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error(i18n.t("jitsiEmbed.loadFailed"))),
      );
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = `https://${domain}/external_api.js`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(i18n.t("jitsiEmbed.loadFailed")));
    document.head.appendChild(s);
  });
}

function parseJitsiUrl(
  url: string,
): { domain: string; roomName: string } | null {
  try {
    const u = new URL(url);
    const room = u.pathname.replace(/^\//, "").split("/")[0];
    if (!room) return null;
    return { domain: u.host, roomName: decodeURIComponent(room) };
  } catch {
    return null;
  }
}

type LoadStatus = "loading" | "ready" | "error" | "invalid";

export function JitsiEmbed(props: JitsiEmbedProps) {
  const {
    externalUrl,
    token,
    user,
    onJoin,
    onLeave,
    onClose,
    onApiReady,
    onEndpointTextMessage,
    className,
  } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const apiRef = useRef<JitsiApi | null>(null);
  const onJoinRef = useRef(onJoin);
  onJoinRef.current = onJoin;
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onApiReadyRef = useRef(onApiReady);
  onApiReadyRef.current = onApiReady;
  const onEndpointTextMessageRef = useRef(onEndpointTextMessage);
  onEndpointTextMessageRef.current = onEndpointTextMessage;
  // Token ve kullanıcı bilgisini ref'te tut. Backend her detay isteğinde YENİ
  // bir JWT üretiyor: kullanıcı Jitsi iframe'ine tıklayıp (window blur) sonra
  // sayfaya geri dönünce (window focus) detay tazeleniyor ve token string'i
  // değişiyor. Bunu effect dependency yaparsak aktif toplantı dispose+recreate
  // olur → kullanıcı yayından kopar, yeniden giriş yapar. Oda (externalUrl)
  // aynı kaldığı sürece Jitsi'yi yeniden başlatmıyoruz.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const userRef = useRef(user);
  userRef.current = user;
  const [status, setStatus] = useState<LoadStatus>("loading");

  useEffect(() => {
    const parsed = parseJitsiUrl(externalUrl);
    if (!parsed) {
      setStatus("invalid");
      return;
    }
    let cancelled = false;
    const activeToken = tokenRef.current;
    const activeUser = userRef.current;
    const sendJwt = !PUBLIC_JITSI_DOMAINS.has(parsed.domain);
    setStatus("loading");

    void loadJitsiScript(parsed.domain)
      .then(() => {
        if (cancelled) return;
        const Ctor = window.JitsiMeetExternalAPI;
        if (!containerRef.current || !Ctor) {
          setStatus("error");
          return;
        }
        // Önceki instance kalmışsa temizle (StrictMode / hot reload).
        if (apiRef.current) {
          try {
            apiRef.current.dispose();
          } catch {
            // ignore
          }
          apiRef.current = null;
        }
        const api = new Ctor(parsed.domain, {
          roomName: parsed.roomName,
          parentNode: containerRef.current,
          width: "100%",
          height: "100%",
          ...(sendJwt && activeToken ? { jwt: activeToken } : {}),
          ...(activeUser && (activeUser.displayName || activeUser.email)
            ? {
                userInfo: {
                  ...(activeUser.displayName
                    ? { displayName: activeUser.displayName }
                    : {}),
                  ...(activeUser.email ? { email: activeUser.email } : {}),
                },
              }
            : {}),
          configOverwrite: {
            prejoinPageEnabled: false,
            prejoinConfig: { enabled: false },
            disableDeepLinking: true,
            startWithAudioMuted: true,
            // Önizleme ekranı kapalı olduğu için sayfa açılır açılmaz kullanıcı
            // odaya düşüyor; görüntü de açık başlarsa kursiyerin kamerası daha
            // "Katıl"a basmadan yayına giriyordu. Kamerayı kullanıcı açar.
            startWithVideoMuted: true,
          },
          interfaceConfigOverwrite: {
            MOBILE_APP_PROMO: false,
            SHOW_JITSI_WATERMARK: false,
            SHOW_PROMOTIONAL_CLOSE_PAGE: false,
            // TOOLBAR_BUTTONS bilinçli olarak verilmiyor: sabit liste rol
            // değişiminde yeniden hesaplanmadığı için toplantı içinde
            // moderatör yapılan kursiyer beyaz tahta butonunu hiç göremiyordu.
            // Jitsi butonu zaten yalnızca moderatöre gösterir.
          },
        });
        apiRef.current = api;
        setStatus("ready");
        const handleJoined = () => {
          onJoinRef.current?.();
        };
        const handleLeft = () => {
          onLeaveRef.current?.();
        };
        const handleReadyToClose = () => {
          onLeaveRef.current?.();
          onCloseRef.current?.();
        };
        const handleEndpointTextMessage = (payload: unknown) => {
          const message = payload as {
            senderInfo?: { id?: string };
            eventData?: { text?: unknown };
          };
          const text = message?.eventData?.text;
          if (typeof text === "string") {
            onEndpointTextMessageRef.current?.(
              text,
              message.senderInfo?.id ?? null,
            );
          }
        };
        onApiReadyRef.current?.(api);
        try {
          api.addListener?.("videoConferenceJoined", handleJoined);
          api.addListener?.("endpointTextMessageReceived", handleEndpointTextMessage);
          api.addListener?.("readyToClose", handleReadyToClose);
          api.addListener?.("videoConferenceLeft", handleLeft);
        } catch {
          // ignore
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
      if (apiRef.current) {
        try {
          apiRef.current.dispose();
        } catch {
          // ignore
        }
        apiRef.current = null;
        onApiReadyRef.current?.(null);
        // dispose() videoConferenceLeft yaymıyor; odadan çıkışı burada bildir.
        onLeaveRef.current?.();
      }
    };
    // Yalnızca oda (externalUrl) değişince yeniden başlat. token/user kasıtlı
    // olarak hariç: tazelenen JWT aktif toplantıyı koparmasın (refs üzerinden
    // okunuyor). Kullanıcı başka bir Jitsi objective'ine geçerse externalUrl
    // değişeceği için doğru şekilde re-init olur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalUrl]);

  return (
    <div className={className} style={{ position: "relative" }}>
      <div
        ref={containerRef}
        style={{ width: "100%", height: "100%" }}
      />
      {status !== "ready" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            background: "rgba(0,0,0,0.7)",
            borderRadius: 6,
            textAlign: "center",
            padding: 16,
            fontSize: 14,
          }}
        >
          {status === "loading" && i18n.t("jitsiEmbed.loading")}
          {status === "error" &&
            i18n.t("jitsiEmbed.loadError")}
          {status === "invalid" && i18n.t("jitsiEmbed.invalid")}
        </div>
      )}
    </div>
  );
}

